import { memo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { MenuSmallIcon } from '@/icons';
import { showImageUploadModal } from '@/shared/features/image-upload/components';
import { ITEM_FORMULA_LATEX_MAX_LENGTH } from '@/shared/features/rich-text/utils/countQuizText';
import { cn } from '@/utils/tailwind.util';
import { useImageUploadInput } from '@/views/build/hook/useImageUploadInput';
import { type BuildSchemaInputType } from '../../../form/schema';
import { useFocusTarget } from '../../focus/useFocusTarget';
import { DeleteButton } from '../DeleteButton';
import { EditTools } from '../EditTools';
import { HeightAnimate } from '../HeightAnimate';
import { useDroppableImageUploadInput } from '../hook/useDroppableImageUploadInput';
import { usePasteFile } from '../hook/usePasteFile';
import { useQuizFormField } from '../hook/useQuizFormField';
import { MultipleOptionImage } from '../Multiple/MultipleOptionImage';
import { useOptionSortable } from '../Multiple/useOptionSortable';
import { useQuizControl } from '../QuizControlProvider';
import { TextEditor } from '../TextEditor';
import { useOrderItemTextEditor } from './useOrderItemTextEditor';

/** Figma 의 보기 placeholder — 서수 표기라 객관식의 "{{n}}번 보기"를 재사용할 수 없다. */
const ORDER_ITEM_PLACEHOLDER_KEYS = ['build:build.order.itemPlaceholder.first', 'build:build.order.itemPlaceholder.second', 'build:build.order.itemPlaceholder.third', 'build:build.order.itemPlaceholder.fourth', 'build:build.order.itemPlaceholder.fifth'] as const;
type EditableOrderItemProps = {
  id: string;
  itemIndex: number;
  isEditMode: boolean;
  /** 최소 개수(2개)일 때는 주지 않는다 → 삭제 버튼이 사라진다. */
  onRemove?: () => void;
};
export const EditableOrderItem = memo(({
  id,
  itemIndex,
  isEditMode,
  onRemove
}: EditableOrderItemProps) => {
  const {
    index
  } = useQuizControl();
  const {
    t
  } = useTranslation(['build']);
  const {
    getValues,
    setValue
  } = useFormContext<BuildSchemaInputType>();
  const [imageUrl, updateImageUrl] = useQuizFormField({
    field: `orderData.items.${itemIndex}.imageUrl`
  });
  const itemRef = useFocusTarget<HTMLDivElement>(`quizList.${index}.orderData.items.${itemIndex}.text`);
  const {
    editor
  } = useOrderItemTextEditor({
    itemIndex
  });
  const updateImageUrlById = useCallback((uploadedUrl: string) => {
    const items = getValues(`quizList.${index}.orderData.items`);
    const currentIndex = items.findIndex(item => item.id === id);
    if (currentIndex < 0) return;
    setValue(`quizList.${index}.orderData.items.${currentIndex}.imageUrl`, uploadedUrl, {
      shouldDirty: true
    });
  }, [getValues, id, index, setValue]);
  const {
    attributes,
    listeners,
    setNodeRef,
    style,
    isDragging
  } = useOptionSortable({
    id
  });
  const {
    handleFileChange,
    accept,
    isUploading
  } = useImageUploadInput({
    onUploadComplete: updateImageUrlById
  });
  const handleFileChangeVoid = (file: File | null) => {
    void handleFileChange(file);
  };
  const {
    rootProps,
    inputProps,
    isDragActive,
    inputRef
  } = useDroppableImageUploadInput({
    onFileChange: handleFileChangeVoid,
    accept,
    imageUrl,
    disabled: false
  });
  usePasteFile({
    editor,
    shouldConfirm: !!imageUrl,
    onPaste: handleFileChangeVoid
  });
  return <li {...listeners} {...attributes} ref={setNodeRef} style={style} onClick={e => e.stopPropagation()} className={cn('group relative flex w-full items-center gap-xs pl-none pr-lg', isDragging && 'shadow-md', isEditMode && 'cursor-default')}>
        <div className="flex shrink-0 items-center gap-4xs self-start py-xs">
          <MenuSmallIcon size="xs" fill={ICON_COLOR.alternative} className={cn('absolute -translate-x-[18px] opacity-0 transition-opacity duration-200 group-hover:opacity-100', 'cursor-grab', isDragging && 'cursor-grabbing')} />
          <span aria-hidden className={cn('w-[30px] text-center text-heading-sm-bold text-assistive', isEditMode && 'group-focus-within:text-strong')}>
            {itemIndex + 1}
          </span>
        </div>
        <div className="flex min-w-px flex-1 flex-col items-start justify-center gap-2xs">
          <HeightAnimate {...rootProps} className="relative w-full">
            <input {...inputProps} className="hidden" ref={inputRef} />
            <TextEditor.Input ref={itemRef} placeholder={t(ORDER_ITEM_PLACEHOLDER_KEYS[itemIndex] ?? ORDER_ITEM_PLACEHOLDER_KEYS[0])} placeholderClassName="text-heading-sm-bold" editor={editor} editorClassName="text-heading-sm-bold" className={cn('text-heading-sm-bold', isDragActive && 'shadow-[inset_0_-2px_0_0_theme(colors.violet.300)]')} onClick={e => e.stopPropagation()} />
            <EditTools.InlineTooltipContainer className="hidden group-focus-within:flex">
              <TextEditor.EquationButton editor={editor} maxLength={ITEM_FORMULA_LATEX_MAX_LENGTH} />
              <EditTools.Divider />
              <EditTools.ImageAddButton onClick={() => {
            showImageUploadModal({
              onUploadStart: handleFileChangeVoid,
              place: 'build',
              imageUrl
            });
          }} />
            </EditTools.InlineTooltipContainer>
          </HeightAnimate>
          {imageUrl && <MultipleOptionImage imageUrl={imageUrl} isUploading={isUploading} onRemove={() => updateImageUrl(undefined)} className="ml-3xs" />}
        </div>
        {onRemove && isEditMode && <div className="flex w-[30px] shrink-0 items-start justify-center self-start py-2xs">
            <DeleteButton onClick={onRemove} />
          </div>}
      </li>;
});